import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AvatarWithName, Typography, VerifiedIcon } from '@universal-tennis/ui-shared';
import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';

import GenericTable from '../../GenericTable';
import MessageIcon from '../../../Icons/MessageIcon';
import PlayerUtr from '../../../PlayerUtr';
import MoreMenu from '../../../MoreMenu';
import { getPlayerDisplayUtr, getDisplayUTRPRating } from '../../../../utils/display';
import { PlayerManageOptions, PlayerActionTypes, SportTypeIds } from '../../../../utils/types';
import {
    teamProfilePickleballHeadCells,
    teamProfileTennisHeadCells,
    SharedUICategories,
    TypographySizes,
} from '../../../../utils/constants';
import { getPlayerAvatarImage, getPlayerProfileUrl } from '../../../../utils/helpers';
import { playerTablePropTypes, playersTableRowPropTypes } from '../../../../utils/proptypes';

// This component will replace all PlayerTable components once the new designs are gradually added

const PlayersTable = ({
    teamPlayers = [],
    isLoading,
    onWithdrawPlayer,
    onMovePlayer,
    onCopyPlayer,
    onTogglePlayerStatus,
    isManaging = false,
    isSubstitutePlayers = false,
    hasSessionEnded,
}) => {
    const theme = useTheme();
    const { SECONDARY } = SharedUICategories;
    const { X_SMALL_BOOK } = TypographySizes;
    const { WITHDRAW, MOVE_TO_TEAM, COPY_TO_TEAM, MARK_AS_ROSTERED, MARK_AS_SUBSTITUTE } = PlayerActionTypes;
    const [players, setPlayers] = useState([]);
    const { sportTypeId } = useSelector((state) => state.conferenceDetails);
    const isTennisMatch = sportTypeId === SportTypeIds.TENNIS;

    useEffect(() => {
        const formattedPlayers = teamPlayers.map((player) => {
            const { isVerified, rating } = getPlayerDisplayUtr(player);
            const { isVerified: isVerifiedDoubles, rating: doublesUtr } = getPlayerDisplayUtr(player, true);
            const {
                memberId,
                teamId,
                firstName,
                lastName,
                playerId,
                hasPostedScores,
                canBeMovedOrCopied,
                canBeRefunded,
                isSubstitute,
                profileImage,
                record,
            } = player || {};
            return {
                id: memberId,
                playerId,
                teamId,
                name: `${firstName} ${lastName}`,
                doublesUtr,
                isVerifiedDoubles,
                singlesUtr: rating,
                isVerifiedSingles: isVerified,
                hasPostedScores,
                canBeMovedOrCopied,
                isSubstitute,
                canBeRefunded,
                profileImage,
                singlesUTRP: getDisplayUTRPRating(player),
                doublesUTRP: getDisplayUTRPRating(player, true),
                singlesRecord: `${record?.singlesWins}-${record?.singlesLosses}`,
                doublesRecord: `${record?.doublesWins}-${record?.doublesLosses}`,
            };
        });
        setPlayers(formattedPlayers);
    }, [teamPlayers]);

    const PlayerTableRow = ({ rowData, labelId }) => {
        const {
            doublesUtr,
            id,
            isVerifiedDoubles,
            isVerifiedSingles,
            name,
            profileImage,
            playerId,
            singlesUtr,
            teamId,
            hasPostedScores,
            canBeMovedOrCopied,
            isSubstitute,
            canBeRefunded,
            singlesUTRP,
            doublesUTRP,
            singlesRecord,
            doublesRecord,
        } = rowData;

        PlayerTableRow.propTypes = {
            ...playersTableRowPropTypes,
        };

        const handleOnMenuOptionSelected = (selectedOption) => {
            switch (selectedOption) {
                case WITHDRAW:
                    onWithdrawPlayer(id, teamId, canBeRefunded);
                    break;
                case MOVE_TO_TEAM:
                    onMovePlayer(id, teamId);
                    break;
                case COPY_TO_TEAM:
                    onCopyPlayer(id, teamId);
                    break;
                case MARK_AS_ROSTERED:
                case MARK_AS_SUBSTITUTE:
                    onTogglePlayerStatus(id, teamId);
                    break;
                default:
                    break;
            }
        };

        const avatarImage = getPlayerAvatarImage(profileImage);
        const playerUrl = getPlayerProfileUrl(playerId);

        return (
            <TableRow tabIndex={-1} key={id} sx={{ backgroundColor: 'white' }}>
                <TableCell sx={{ color: 'black', border: 'none' }} component="th" id={labelId} scope="row" padding="normal">
                    <Stack>
                        <AvatarWithName href={playerUrl} imgSrc={avatarImage} playerName={name} />
                    </Stack>
                </TableCell>
                {isTennisMatch ? (
                    <>
                        <TableCell sx={{ border: 'none' }} align="center">
                            <PlayerUtr utr={singlesUtr} isVerified={isVerifiedSingles} isUpdatedDesign />
                        </TableCell>
                        <TableCell sx={{ border: 'none' }} align="center">
                            <PlayerUtr utr={doublesUtr} isVerified={isVerifiedDoubles} isUpdatedDesign />
                        </TableCell>
                    </>
                ) : (
                    <>
                        <TableCell sx={{ border: 'none' }} align="center">
                            <Box maxWidth="50px" m="0 auto" textAlign="left">
                                <Box display="flex" alignItems="center">
                                    <Typography color={theme.appColors.black} category={SECONDARY} size={X_SMALL_BOOK} mr={0.5}>
                                        {singlesUTRP?.rating}
                                    </Typography>
                                    {singlesUTRP?.displayVerifiedIcon && <VerifiedIcon size={12} color={theme.appColors.lighterGreen} />}
                                </Box>
                                {singlesUTRP?.reliability !== 'Reliable' && (
                                    <Typography color={theme.appColors.grey} category={SECONDARY} size={X_SMALL_BOOK}>
                                        {singlesUTRP?.reliability}
                                    </Typography>
                                )}
                            </Box>
                        </TableCell>
                        <TableCell sx={{ border: 'none' }} align="center">
                            <Box maxWidth="55px" m="0 auto" textAlign="left">
                                <Box display="flex" alignItems="center">
                                    <Typography color={theme.appColors.black} category={SECONDARY} size={X_SMALL_BOOK} mr={0.5}>
                                        {doublesUTRP?.rating}
                                    </Typography>
                                    {doublesUTRP?.displayVerifiedIcon && <VerifiedIcon size={12} color={theme.appColors.lighterGreen} />}
                                </Box>
                                {doublesUTRP?.reliability !== 'Reliable' && (
                                    <Typography color={theme.appColors.grey} category={SECONDARY} size={X_SMALL_BOOK}>
                                        {doublesUTRP?.reliability}
                                    </Typography>
                                )}
                            </Box>
                        </TableCell>
                    </>
                )}

                <TableCell sx={{ border: 'none' }} align="center">
                    <Typography color={theme.appColors.black} category={SECONDARY} size={X_SMALL_BOOK}>
                        {singlesRecord}
                    </Typography>
                </TableCell>
                <TableCell sx={{ border: 'none' }} align="center">
                    <Typography color={theme.appColors.black} category={SECONDARY} size={X_SMALL_BOOK}>
                        {doublesRecord}
                    </Typography>
                </TableCell>
                <TableCell sx={{ border: 'none' }} align="center">
                    <Stack direction="row" spacing={3}>
                        <IconButton title="Message Captain" aria-label="message captain">
                            <MessageIcon memberId={id} displayName={name} style={{ marginLeft: 'auto' }} />
                        </IconButton>
                        {isManaging && (
                            <MoreMenu
                                disableMove={hasPostedScores || !canBeMovedOrCopied}
                                disableCopy={hasSessionEnded || !canBeMovedOrCopied}
                                options={PlayerManageOptions}
                                isSubstitute={isSubstitute}
                                onOptionSelected={handleOnMenuOptionSelected}
                            />
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
        );
    };

    const nameHeadCell = { id: 'name', label: isSubstitutePlayers ? 'Substitute Player' : 'Rostered Player' };
    const headCells = isTennisMatch ? [nameHeadCell, ...teamProfileTennisHeadCells] : [nameHeadCell, ...teamProfilePickleballHeadCells];

    return <GenericTable sortBy="name" tableData={players} headCells={headCells} isLoading={isLoading} rowComponent={PlayerTableRow} />;
};

PlayersTable.propTypes = {
    ...playerTablePropTypes,
};

export default PlayersTable;
